import {
  EntryTypeCover,
  EntryTypeFree,
  EntryTypeRSVP,
  EntryTypeTicketed,
  EventAgeRestriction18Plus,
  EventAgeRestriction21Plus,
  EventAgeRestrictionAllAges,
  FeedItemTypeDeal,
  type FeedEvent,
  type FeedItem,
  type EntryType,
  type EventAgeRestriction,
} from '@/lib/api/apiTypes';
import type { FeedDistanceFilter, FeedTimeWindow } from '@/features/events/feed/feedPreferences';

export type FeedTagGroup =
  | 'music'
  | 'dancing'
  | 'low_key'
  | 'food_drink'
  | 'arts_culture'
  | 'outside'
  | 'social'
  | 'late_night';
export type FeedPreset = 'all' | FeedTagGroup;
export type EntryTypeFilter = 'all' | EntryType;
export type FeedBudgetFilter = 'any' | 'under_15' | 'under_30';
export type AttributeQuickFilter = 'outdoor' | 'accessible' | 'food_available';
export type AgeAccessFilter =
  | typeof EventAgeRestriction21Plus
  | typeof EventAgeRestriction18Plus
  | typeof EventAgeRestrictionAllAges;
export type FeedAccessFilter = AttributeQuickFilter | AgeAccessFilter;

const TAG_GROUP_MATCHES: Record<FeedTagGroup, string[]> = {
  music: [
    'live_music',
    'dj',
    'hip_hop',
    'electronic',
    'latin',
    'country',
    'jazz',
    'reggaeton',
    'indie',
    'metal',
    'karaoke',
    'tejano',
    'cumbia',
    'salsa',
    'bachata',
  ],
  dancing: ['dj', 'club_night', 'latin', 'reggaeton', 'salsa', 'cumbia', 'bachata'],
  low_key: ['trivia', 'bingo', 'game_night', 'open_mic', 'jazz', 'board_games', 'comedy'],
  food_drink: [
    'happy_hour',
    'brunch',
    'food_trucks',
    'cocktails',
    'craft_beer',
    'wine_tasting',
    'open_bar',
    'free_food',
    'byob',
    'food',
  ],
  arts_culture: ['art_show', 'gallery', 'theater', 'poetry', 'film', 'pop_up', 'art'],
  outside: ['rooftop', 'pool_party', 'day_party', 'outdoor'],
  social: ['meetup', 'networking', 'market', 'fundraiser', 'festival'],
  late_night: ['after_party', 'club_night', 'industry_night', 'dj'],
};

export const TIME_FILTERS: { label: string; value: FeedTimeWindow }[] = [
  { label: 'Now', value: 'now' },
  { label: 'Today', value: 'today' },
  { label: 'Tonight', value: 'tonight' },
  { label: 'Tomorrow', value: 'tomorrow' },
  { label: 'This Weekend', value: 'weekend' },
  { label: 'This Week', value: 'week' },
];

export const DISTANCE_FILTERS: { label: string; radiusMeters: FeedDistanceFilter }[] = [
  { label: '1 mi', radiusMeters: 1600 },
  { label: '5 mi', radiusMeters: 8000 },
  { label: '10 mi', radiusMeters: 16000 },
  { label: '25 mi', radiusMeters: 40000 },
  { label: 'Any distance', radiusMeters: 80000 },
];

export const FEED_PRESETS: { label: string; value: FeedTagGroup }[] = [
  { label: 'Music', value: 'music' },
  { label: 'Dancing', value: 'dancing' },
  { label: 'Low-key', value: 'low_key' },
  { label: 'Food & drinks', value: 'food_drink' },
  { label: 'Arts & culture', value: 'arts_culture' },
  { label: 'Outside', value: 'outside' },
  { label: 'Social', value: 'social' },
  { label: 'Late night', value: 'late_night' },
];

export const ENTRY_TYPE_FILTERS: { label: string; value: EntryTypeFilter }[] = [
  { label: 'All', value: 'all' },
  { label: 'Free', value: EntryTypeFree },
  { label: 'Cover', value: EntryTypeCover },
  { label: 'RSVP', value: EntryTypeRSVP },
  { label: 'Ticketed', value: EntryTypeTicketed },
];

export const ATTRIBUTE_QUICK_FILTERS: { label: string; value: AttributeQuickFilter }[] = [
  { label: 'Outdoor', value: 'outdoor' },
  { label: 'Accessible', value: 'accessible' },
  { label: 'Food available', value: 'food_available' },
];

export const BUDGET_FILTERS: {
  label: string;
  maxPriceCents: number | null;
  value: FeedBudgetFilter;
}[] = [
  { label: 'Any price', value: 'any', maxPriceCents: null },
  { label: 'Under $15', value: 'under_15', maxPriceCents: 1500 },
  { label: 'Under $30', value: 'under_30', maxPriceCents: 3000 },
];

export const ACCESS_FILTERS: { label: string; value: FeedAccessFilter }[] = [
  { label: 'Outdoor', value: 'outdoor' },
  { label: 'Accessible', value: 'accessible' },
  { label: 'Food available', value: 'food_available' },
  { label: '21+', value: EventAgeRestriction21Plus },
  { label: '18+', value: EventAgeRestriction18Plus },
  { label: 'All ages', value: EventAgeRestrictionAllAges },
];

export function getFeedPresetLabel(value: FeedPreset) {
  return value === 'all'
    ? 'All'
    : (FEED_PRESETS.find((filter) => filter.value === value)?.label ?? 'All');
}

export function getEntryTypeFilterLabel(value: EntryTypeFilter) {
  return ENTRY_TYPE_FILTERS.find((filter) => filter.value === value)?.label ?? 'All';
}

export function getAttributeQuickFilterLabel(value: AttributeQuickFilter) {
  return ATTRIBUTE_QUICK_FILTERS.find((filter) => filter.value === value)?.label ?? value;
}

export function getAccessFilterLabel(value: FeedAccessFilter) {
  return ACCESS_FILTERS.find((filter) => filter.value === value)?.label ?? value;
}

export function getBudgetFilterLabel(value: FeedBudgetFilter) {
  return BUDGET_FILTERS.find((filter) => filter.value === value)?.label ?? 'Any price';
}

export function getBudgetFilterForState(
  entryTypeFilter: EntryTypeFilter,
  maxPriceCents: number | null
): FeedBudgetFilter {
  if (entryTypeFilter === EntryTypeFree) {
    return 'any';
  }
  if (maxPriceCents === 1500) {
    return 'under_15';
  }
  if (maxPriceCents === 3000) {
    return 'under_30';
  }
  return 'any';
}

export function getAttributeFiltersForAccess(filters: FeedAccessFilter[]) {
  return filters.filter(isAttributeAccessFilter);
}

export function getAgeFilterForAccess(filters: FeedAccessFilter[]) {
  return filters.find(isAgeAccessFilter) ?? null;
}

export function getTimeFilterLabel(value: FeedTimeWindow) {
  if (value === 'all') {
    return 'Any Time';
  }
  return TIME_FILTERS.find((filter) => filter.value === value)?.label ?? 'Today';
}

export function getDistanceFilterLabel(radiusMeters: FeedDistanceFilter) {
  return DISTANCE_FILTERS.find((filter) => filter.radiusMeters === radiusMeters)?.label ?? '10 mi';
}

export function filterFeedItemsByPreset(
  items: FeedItem[],
  preset: FeedPreset,
  entryTypeFilter: EntryTypeFilter,
  activeAccessFilters: FeedAccessFilter[],
  maxPriceCents: number | null = null
) {
  return items.filter((item) => {
    if (item.type === FeedItemTypeDeal && item.deal) {
      if (
        preset !== 'all' ||
        entryTypeFilter !== 'all' ||
        activeAccessFilters.length > 0 ||
        maxPriceCents !== null
      ) {
        return false;
      }
      return true;
    }

    if (!item.event) {
      return false;
    }

    return matchesFeedEventFilters(item.event, {
      accessFilters: activeAccessFilters,
      entryTypeFilter,
      maxPriceCents,
      preset,
    });
  });
}

export function matchesFeedEventFilters(
  event: FeedEvent,
  {
    accessFilters,
    entryTypeFilter,
    maxPriceCents,
    preset,
  }: {
    accessFilters: FeedAccessFilter[];
    entryTypeFilter: EntryTypeFilter;
    maxPriceCents: number | null;
    preset: FeedPreset;
  }
) {
  if (preset !== 'all' && !matchesFeedPreset(event, preset)) {
    return false;
  }

  if (entryTypeFilter !== 'all' && event.entry_type !== entryTypeFilter) {
    return false;
  }

  if (maxPriceCents !== null && event.entry_type !== EntryTypeFree) {
    const maxPrice = maxPriceCents / 100;
    if (typeof event.cover_price === 'number' && event.cover_price > maxPrice) {
      return false;
    }
  }

  const attributeFilters = getAttributeFiltersForAccess(accessFilters);
  if (
    attributeFilters.length > 0 &&
    !attributeFilters.every((filter) => event.attributes.includes(filter))
  ) {
    return false;
  }

  const ageFilter = getAgeFilterForAccess(accessFilters);
  if (ageFilter && event.age_restriction !== ageFilter) {
    return false;
  }

  return true;
}

function matchesFeedPreset(event: FeedEvent, preset: FeedPreset) {
  if (preset === 'all') {
    return true;
  }

  const tags = event.tags.map((tag) => tag.toLowerCase());
  const matches = TAG_GROUP_MATCHES[preset];
  return matches.some((tag) => tags.includes(tag));
}

function isAttributeAccessFilter(value: FeedAccessFilter): value is AttributeQuickFilter {
  return value === 'outdoor' || value === 'accessible' || value === 'food_available';
}

function isAgeAccessFilter(value: FeedAccessFilter): value is EventAgeRestriction {
  return (
    value === EventAgeRestriction21Plus ||
    value === EventAgeRestriction18Plus ||
    value === EventAgeRestrictionAllAges
  );
}
